const POPOVER_ID = 'listing-cost-popover';
const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 220;

let activeTrigger = null;
let pinnedTrigger = null;
let hideTimer = null;
let globalEventsBound = false;
let fallbackPopoverHost = null;

export function createCostBadge(cost) {
  if (!cost || typeof cost !== 'object') {
    return createCostPlaceholder();
  }
  const total = formatCurrency(cost.totalCny || 0);
  const detail = buildCostDetail(cost, total);
  return createDetailBadge(detail, total);
}

export function createUsageCostBadge(usage = {}) {
  const costs = usage?.costs;
  if (!costs || typeof costs !== 'object') {
    return createCostPlaceholder();
  }
  const total = formatCurrency(costs.totalCny || 0);
  const detail = {
    total,
    imageCost: formatCurrency(costs.linkfoxCny || 0),
    imageUnits: Number(usage.linkfoxCredits ?? usage.totalImagesGenerated ?? 0).toLocaleString('zh-CN'),
    marketCost: formatCurrency(costs.marketDataCny || 0),
    marketCalls: Number(usage.sellerspriteCalls ?? usage.totalSellerspriteCalls ?? 0).toLocaleString('zh-CN'),
    modelCost: formatCurrency(costs.textModelCny || 0),
    modelInputTokens: Number(usage.textInputTokens ?? usage.totalTextInputTokens ?? 0).toLocaleString('zh-CN'),
    modelOutputTokens: Number(usage.textOutputTokens ?? usage.totalTextOutputTokens ?? 0).toLocaleString('zh-CN'),
    modelPricing: buildUsageModelSummary(usage),
    attributionNote: usage?.name
      ? `${usage.name} 在当前筛选区间内的用户维度消耗汇总`
      : '当前筛选区间内的系统维度消耗汇总',
  };
  return createDetailBadge(detail, total);
}

function createCostPlaceholder() {
  const placeholder = document.createElement('span');
  placeholder.className = 'mono listing-cost-empty';
  placeholder.textContent = '—';
  return placeholder;
}

function createDetailBadge(detail, total) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'listing-cost-badge mono';
  button.dataset.costDetail = JSON.stringify(detail);
  button.setAttribute('aria-haspopup', 'dialog');
  button.setAttribute('aria-expanded', 'false');
  button.textContent = total;
  return button;
}

export function bindCostDetailPopovers(root = document) {
  bindGlobalEvents();
  root.querySelectorAll('.listing-cost-badge[data-cost-detail]').forEach((trigger) => {
    if (trigger.dataset.costPopoverBound === '1') return;
    trigger.dataset.costPopoverBound = '1';
    trigger.addEventListener('mouseenter', () => showCostPopover(trigger, { pinned: false }));
    trigger.addEventListener('focus', () => showCostPopover(trigger, { pinned: false }));
    trigger.addEventListener('mouseleave', () => {
      if (pinnedTrigger !== trigger) scheduleHide();
    });
    trigger.addEventListener('blur', () => {
      if (pinnedTrigger !== trigger) scheduleHide();
    });
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (pinnedTrigger === trigger) {
        hideCostPopover();
        return;
      }
      showCostPopover(trigger, { pinned: true });
    });
  });
}

function buildCostDetail(cost, total) {
  return {
    total,
    imageCost: formatCurrency(cost.image?.costCny || 0),
    imageUnits: Number(cost.image?.units || 0).toLocaleString('zh-CN'),
    marketCost: formatCurrency(cost.market?.costCny || 0),
    marketCalls: Number(cost.market?.calls || 0).toLocaleString('zh-CN'),
    modelCost: formatCurrency(cost.model?.costCny || 0),
    modelInputTokens: Number(cost.model?.inputTokens || 0).toLocaleString('zh-CN'),
    modelOutputTokens: Number(cost.model?.outputTokens || 0).toLocaleString('zh-CN'),
    modelTotalTokens: Number(cost.model?.totalTokens || 0).toLocaleString('zh-CN'),
    modelPricing: summarizeModelPricing(cost.model?.models || []),
    attributionNote: cost.attribution?.note || '未命中可归属消耗',
  };
}

function summarizeModelPricing(models = []) {
  if (!Array.isArray(models) || models.length === 0) return '无模型消耗';
  return models.slice(0, 3).map((item) => {
    const modelName = item.modelName || item.provider || '-';
    const matchLabel = item.matchedBy === 'exact'
      ? '精确匹配'
      : item.matchedBy === 'provider'
        ? '供应商统一价'
        : '未命中单价';
    return `${modelName} · ${matchLabel}`;
  }).join('；');
}

function buildUsageModelSummary(usage = {}) {
  const minimaxTokens = Number(usage.minimaxTotalTokens ?? usage.totalMinimaxTokens ?? 0);
  const textTokens = Number(usage.textTotalTokens ?? usage.totalTextTokens ?? 0);
  if (textTokens <= 0) return '无模型消耗';
  if (minimaxTokens <= 0) return 'Gemini 汇总';
  if (minimaxTokens >= textTokens) return 'MiniMax 汇总';
  return 'Gemini + MiniMax 汇总';
}

function parseCostDetail(trigger) {
  try {
    return JSON.parse(trigger.dataset.costDetail || '{}');
  } catch {
    return {};
  }
}

function createCostPopoverElement(tagName, { className = '', text = '', attrs = {} } = {}) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attrs)) {
    element.setAttribute(name, String(value));
  }
  return element;
}

function createCostPopoverRow(label, cost, details = []) {
  const row = createCostPopoverElement('div', { className: 'listing-cost-popover-row' });
  row.append(
    createCostPopoverElement('span', { text: label }),
    createCostPopoverElement('strong', { text: cost }),
  );
  for (const text of details) {
    row.append(createCostPopoverElement('small', { text }));
  }
  return row;
}

function createCostDetailView(detail = {}) {
  const fragment = document.createDocumentFragment();
  const head = createCostPopoverElement('div', { className: 'listing-cost-popover-head' });
  const summary = document.createElement('div');
  summary.append(
    createCostPopoverElement('div', { className: 'listing-cost-popover-label', text: '总成本' }),
    createCostPopoverElement('div', { className: 'listing-cost-popover-total', text: detail.total || '¥0.00' }),
  );
  const close = createCostPopoverElement('button', {
    className: 'listing-cost-popover-close',
    text: '×',
    attrs: { type: 'button', 'data-cost-popover-close': '', 'aria-label': '关闭' },
  });
  head.append(summary, close);

  const grid = createCostPopoverElement('div', { className: 'listing-cost-popover-grid' });
  grid.append(
    createCostPopoverRow('图片算力', detail.imageCost || '¥0.00', [`${detail.imageUnits || '0'} 算力`]),
    createCostPopoverRow('市场工具', detail.marketCost || '¥0.00', [`${detail.marketCalls || '0'} 次`]),
    createCostPopoverRow('模型', detail.modelCost || '¥0.00', [
      `输入 ${detail.modelInputTokens || '0'} / 输出 ${detail.modelOutputTokens || '0'} tokens`,
      `计费 ${detail.modelPricing || '无模型消耗'}`,
    ]),
  );
  fragment.append(
    head,
    grid,
    createCostPopoverElement('div', { className: 'listing-cost-popover-note', text: detail.attributionNote || '' }),
  );
  return fragment;
}

function showCostPopover(trigger, { pinned }) {
  clearTimeout(hideTimer);
  const popover = ensurePopover();
  const previousTrigger = activeTrigger;
  activeTrigger?.setAttribute('aria-expanded', 'false');
  activeTrigger = trigger;
  pinnedTrigger = pinned ? trigger : pinnedTrigger === trigger ? pinnedTrigger : null;
  trigger.setAttribute('aria-expanded', pinned ? 'true' : 'false');
  popover.replaceChildren(createCostDetailView(parseCostDetail(trigger)));
  updateCostPopoverPlacement(trigger, popover);
  if (isNativePopoverOpen(popover) && previousTrigger !== trigger) popover.hidePopover();
  if (!isNativePopoverOpen(popover)) {
    const opening = openCostPopover(popover, trigger);
    if (opening.native) {
      fallbackPopoverHost?.classList.remove('listing-cost-popover-host');
      fallbackPopoverHost = null;
    } else {
      fallbackPopoverHost?.classList.remove('listing-cost-popover-host');
      fallbackPopoverHost = opening.host;
    }
  }
  popover.classList.add('show');
}

function scheduleHide() {
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    if (!pinnedTrigger) hideCostPopover();
  }, 180);
}

function hideCostPopover() {
  clearTimeout(hideTimer);
  activeTrigger?.setAttribute('aria-expanded', 'false');
  pinnedTrigger?.setAttribute('aria-expanded', 'false');
  activeTrigger = null;
  pinnedTrigger = null;
  const popover = document.getElementById(POPOVER_ID);
  popover?.classList.remove('show');
  if (popover && isNativePopoverOpen(popover)) popover.hidePopover();
  fallbackPopoverHost?.classList.remove('listing-cost-popover-host');
  fallbackPopoverHost = null;
}

function ensurePopover() {
  let popover = document.getElementById(POPOVER_ID);
  if (popover) return popover;
  popover = document.createElement('div');
  popover.id = POPOVER_ID;
  popover.className = 'listing-cost-popover';
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('popover', 'manual');
  document.body.appendChild(popover);
  popover.addEventListener('mouseenter', () => clearTimeout(hideTimer));
  popover.addEventListener('mouseleave', () => {
    if (!pinnedTrigger) scheduleHide();
  });
  popover.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.target.closest('[data-cost-popover-close]')) hideCostPopover();
  });
  return popover;
}

function isNativePopoverOpen(popover) {
  try {
    return typeof popover.matches === 'function' && popover.matches(':popover-open');
  } catch {
    return false;
  }
}

export function openCostPopover(popover, trigger) {
  if (typeof popover.showPopover === 'function') {
    try {
      popover.showPopover({ source: trigger });
      return { native: true, host: null };
    } catch {
      // Older engines can expose showPopover without accepting an invoker source.
    }
  }

  const host = trigger.parentElement || null;
  host?.classList.add('listing-cost-popover-host');
  host?.appendChild(popover);
  return { native: false, host };
}

function updateCostPopoverPlacement(trigger, popover) {
  const rect = trigger.getBoundingClientRect();
  const centeredLeft = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
  const shouldOpenAbove = rect.bottom + POPOVER_HEIGHT + 10 > window.innerHeight && rect.top > POPOVER_HEIGHT + 12;
  popover.classList.remove('is-anchor-inline-start', 'is-anchor-inline-end', 'is-anchor-block-start');
  if (centeredLeft < 12) {
    popover.classList.add('is-anchor-inline-start');
  } else if (centeredLeft + POPOVER_WIDTH > window.innerWidth - 12) {
    popover.classList.add('is-anchor-inline-end');
  }
  if (shouldOpenAbove) popover.classList.add('is-anchor-block-start');
}

function bindGlobalEvents() {
  if (globalEventsBound) return;
  globalEventsBound = true;
  document.addEventListener('click', (event) => {
    if (!pinnedTrigger) return;
    if (event.target.closest('.listing-cost-badge') || event.target.closest(`#${POPOVER_ID}`)) return;
    hideCostPopover();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideCostPopover();
  });
  window.addEventListener('scroll', () => {
    if (!activeTrigger) return;
    updateCostPopoverPlacement(activeTrigger, ensurePopover());
  }, true);
  window.addEventListener('resize', () => {
    if (!activeTrigger) return;
    updateCostPopoverPlacement(activeTrigger, ensurePopover());
  });
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '¥0.00';
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
